import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getManagerOps, GetManagerOpsOutputType } from 'zite-endpoints-sdk';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Link } from 'react-router-dom';
import {
  Users, BookOpen, Shield, CalendarDays, MessageSquare, FileCheck,
  Star, Award, ChevronRight, AlertTriangle, Clock,
} from 'lucide-react';
import { isManagerPlus } from '@/lib/roles';

type Data = GetManagerOpsOutputType;

function GapRow({ name, detail, status, link }: { name: string; detail: string; status: string; link: string }) {
  return (
    <Link to={link} className="flex items-center gap-3 py-2 border-b border-border last:border-0 hover:bg-muted/30 transition-colors no-underline px-1">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{name}</p>
        <p className="text-xs text-muted-foreground truncate">{detail}</p>
      </div>
      <Badge variant={status === 'Overdue' || status === 'Expired' ? 'destructive' : 'secondary'} className="text-xs shrink-0">{status}</Badge>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    </Link>
  );
}

export default function ManagerOps() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !isManagerPlus(user.role)) { setLoading(false); return; }
    getManagerOps({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!isManagerPlus(user?.role)) {
    return (
      <div className="px-4 md:px-8 py-12 text-center">
        <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
        <h1 className="text-lg font-semibold">Manager Access Required</h1>
      </div>
    );
  }

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" /> Team Operations Centre
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">{data.activeStaff} active staff · {data.teamSize} total</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { label: 'Training Gaps', value: data.trainingGaps.length, icon: BookOpen, color: data.trainingGaps.length > 0 ? 'text-destructive' : 'text-primary' },
          { label: 'Compliance', value: data.complianceGaps.length, icon: Shield, color: data.complianceGaps.length > 0 ? 'text-destructive' : 'text-primary' },
          { label: 'Pending Leave', value: data.pendingLeave.length, icon: CalendarDays, color: data.pendingLeave.length > 0 ? 'text-accent-foreground' : 'text-primary' },
          { label: 'Requests', value: data.pendingRequests, icon: MessageSquare, color: data.pendingRequests > 0 ? 'text-accent-foreground' : 'text-primary' },
          { label: 'Recognition', value: data.pendingRecognition, icon: Star, color: data.pendingRecognition > 0 ? 'text-accent-foreground' : 'text-primary' },
        ].map(s => (
          <div key={s.label} className="bg-card border border-border rounded-lg p-3 text-center">
            <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue={data.complianceGaps.length > 0 ? 'compliance' : data.pendingLeave.length > 0 ? 'leave' : 'training'}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="training">Training Gaps</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="policies">Policy Acks</TabsTrigger>
          <TabsTrigger value="certs">Expiring Certs</TabsTrigger>
        </TabsList>

        <TabsContent value="training">
          {data.trainingGaps.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <BookOpen className="w-8 h-8 text-primary mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">All staff training is up to date!</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4">
              {data.trainingGaps.map(g => (
                <GapRow key={g.name} name={g.name} detail={`${g.department} · ${g.gaps} module${g.gaps > 1 ? 's' : ''} incomplete`} status={`${g.gaps} gaps`} link="/training-analytics" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="compliance">
          {data.complianceGaps.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <Shield className="w-8 h-8 text-primary mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">No compliance gaps!</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4">
              {data.complianceGaps.map((g, i) => (
                <GapRow key={i} name={g.name} detail={`${g.item} · Due ${g.dueDate}`} status={g.status} link="/compliance-expiry" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leave">
          {data.pendingLeave.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <CalendarDays className="w-8 h-8 text-primary mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">No pending leave requests</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4">
              {data.pendingLeave.map(l => (
                <GapRow key={l.id} name={l.name} detail={`${l.type} · ${l.startDate} — ${l.endDate}`} status="Pending" link="/leave" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="onboarding">
          {data.onboardingProgress.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <Award className="w-8 h-8 text-primary mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">No active onboarding journeys</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              {data.onboardingProgress.map((o, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{o.name}</p>
                    <p className="text-xs text-muted-foreground">{o.journeyName}</p>
                  </div>
                  <div className="w-24">
                    <Progress value={o.progress} className="h-2" />
                  </div>
                  <span className="text-xs text-muted-foreground w-10 text-right">{o.progress}%</span>
                  <Badge variant="secondary" className="text-xs">{o.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="policies">
          {data.policyAckGaps.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <FileCheck className="w-8 h-8 text-primary mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">All policies acknowledged!</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4">
              {data.policyAckGaps.map((g, i) => (
                <GapRow key={i} name={g.name} detail={`${g.missingPolicies} polic${g.missingPolicies > 1 ? 'ies' : 'y'} not acknowledged`} status={`${g.missingPolicies} missing`} link="/policies" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="certs">
          {data.expiringCerts.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <Shield className="w-8 h-8 text-primary mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">No certificates expiring soon</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4">
              {data.expiringCerts.map((c, i) => (
                <GapRow key={i} name={c.name} detail={`${c.cert} · Expires ${c.expiryDate}`} status={c.expiryDate < new Date().toISOString().split('T')[0] ? 'Expired' : 'Expiring'} link="/compliance-certs" />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
